<template>
	<div
		class="relative flex w-full flex-col items-center gap-8 overflow-visible text-white xl:flex-row"
		:class="containerWidthClass"
	>
		<button
			class="absolute left-0 top-1/2 z-10 hidden size-12 -translate-y-1/2 items-center justify-center rounded-full border border-base-200 bg-white text-black transition-colors duration-200 hover:bg-primary hover:text-white lg:-left-8 xl:-left-7 xl:flex"
			@click="prevCard"
		>
			<ArrowLeftIcon class="size-5" />
		</button>
		<button
			class="absolute right-0 top-1/2 z-10 hidden size-12 -translate-y-1/2 items-center justify-center rounded-full border border-base-200 bg-white text-black transition-colors duration-200 hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-black xl:flex"
			:disabled="isLastCard"
			@click="nextCard"
		>
			<ArrowRightIcon class="size-5" />
		</button>
		<div
			class="relative w-full xl:overflow-visible"
			style="clip-path: polygon(0 0, 10000px 0, 10000px 100%, 0 100%)"
		>
			<div
				ref="swiperContainerRef"
				class="swiper-container relative w-full !overflow-visible"
			>
				<div class="swiper-wrapper overflow-visible">
					<!-- Cards 'full' -->
					<template v-if="cardStyle === 'full'">
						<div
							v-for="(card, index) in cardsWithSkills"
							:key="index"
							class="swiper-slide relative !flex shrink-0 flex-col overflow-hidden rounded-lg px-4 py-5 lg:px-7"
							:class="cardClasses"
						>
							<!-- Video background (only if videoUrl exists) -->
							<video
								v-if="card.videoUrl"
								class="absolute inset-0 h-full w-full object-cover"
								autoplay
								muted
								loop
								playsinline
								disablePictureInPicture
								:poster="card.image"
							>
								<source :src="card.videoUrl" type="video/mp4" />
							</video>

							<!-- Image background (if no video) -->
							<div
								v-else
								class="absolute inset-0 h-full w-full object-cover"
								:style="{
									backgroundImage: `url(${card.image})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
								}"
							></div>
							<div class="absolute inset-0 z-[1] bg-black-overlay/40"></div>
							<!-- Content  -->
							<div class="relative z-10">
								<h2 class="text-xl lg:text-2xl">{{ card.name }}</h2>
								<h3 class="text-lg lg:text-xl">{{ card.position }}</h3>
								<h3 class="flex items-center gap-1 text-sm lg:text-base">
									<MapPinIcon class="size-4" />
									{{ card.country }}
								</h3>
							</div>

							<div
								class="relative z-10 hidden flex-col gap-4"
								:class="skillsSectionClasses"
							>
								<h3 class="text-xs font-medium tracking-[2px] text-slate-100">
									{{ $t('SKILL SET') }}
								</h3>
								<div class="flex flex-wrap gap-2">
									<!-- Mobile skills (visible on mobile, hidden on desktop) - only for half-width -->
									<template v-if="isVariantHalfWidth">
										<span
											v-for="skill in card.skillsMobile"
											:key="skill"
											class="rounded-full border border-white px-3 py-1 text-xs font-medium xl:hidden"
											:class="{
												'bg-gray-600': skill === '...',
											}"
										>
											{{ skill }}
										</span>
									</template>

									<!-- Desktop skills for full-width - lg/xl (3 skills) -->
									<template v-if="isVariantFullWidth">
										<span
											v-for="skill in card.skillsDesktopLg"
											:key="skill + 'desktop-lg'"
											class="hidden rounded-full border border-white px-3 py-1 text-xs font-medium lg:block lg:px-4 2xl:hidden"
											:class="{
												'bg-gray-600': skill === '...',
											}"
										>
											{{ skill }}
										</span>
									</template>

									<!-- Desktop skills for full-width - 2xl (6 skills) -->
									<template v-if="isVariantFullWidth">
										<span
											v-for="skill in card.skillsDesktop2xl"
											:key="skill + 'desktop-2xl'"
											class="hidden rounded-full border border-white px-3 py-1 text-xs font-medium lg:px-4 2xl:block"
											:class="{
												'bg-gray-600': skill === '...',
											}"
										>
											{{ skill }}
										</span>
									</template>

									<!-- Desktop skills for half-width - xl (7 skills) -->
									<template v-if="isVariantHalfWidth">
										<span
											v-for="skill in card.skillsDesktop"
											:key="skill + 'desktop'"
											class="hidden rounded-full border border-white px-3 py-1 text-xs font-medium lg:px-4 xl:block 2xl:hidden"
											:class="{
												'bg-gray-600': skill === '...',
											}"
										>
											{{ skill }}
										</span>
									</template>

									<!-- Desktop skills for half-width - 2xl (9 skills) -->
									<template v-if="isVariantHalfWidth">
										<span
											v-for="skill in card.skillsDesktop2xl"
											:key="skill + 'desktop-2xl'"
											class="hidden rounded-full border border-white px-3 py-1 text-xs font-medium lg:px-4 2xl:block"
											:class="{
												'bg-gray-600': skill === '...',
											}"
										>
											{{ skill }}
										</span>
									</template>
								</div>
							</div>
						</div>
					</template>
					<!-- Cards 'compact' -->

					<template v-if="cardStyle === 'compact'">
						<HeroCarouselCardCompact
							v-for="(card, index) in cardsWithSkills"
							:key="index"
							:name="card.name"
							:position="card.position"
							:country="card.country"
							:country-code="card.countryCode"
							:skills="card.skills"
						/>
					</template>
				</div>
			</div>
		</div>

		<!-- Mobile navigation with progress bar -->
		<div
			class="md:px-auto flex w-full max-w-[37.5rem] items-center justify-center gap-3 px-6 xl:hidden"
		>
			<button
				:class="
					isVariantFullWidth
						? 'flex h-8 w-10 items-center justify-center rounded-full bg-white text-black'
						: 'flex h-8 w-10 items-center justify-center rounded-full text-black'
				"
				@click="prevCard"
			>
				<ArrowLeftIcon class="size-5" />
			</button>
			<div
				class="bar relative h-1 w-full max-w-lg"
				:class="isVariantFullWidth ? 'bg-white/20' : 'bg-gray-200'"
			>
				<div
					:class="
						isVariantFullWidth
							? 'absolute h-full bg-white transition-all duration-300'
							: 'absolute h-full bg-black transition-all duration-300'
					"
					:style="progressBarStyle"
				></div>
			</div>
			<button
				:class="[
					isVariantFullWidth
						? 'flex h-8 w-10 items-center justify-center rounded-full bg-white text-black'
						: 'flex h-8 w-10 items-center justify-center rounded-full text-black',
					'transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
				]"
				:disabled="isLastCard"
				@click="nextCard"
			>
				<ArrowRightIcon class="size-5" />
			</button>
		</div>
	</div>
</template>
<script lang="ts" setup>
import HeroCarouselCardCompact from './components/HeroCarouselCardCompact.vue';
import { computed, nextTick, onMounted, ref, shallowRef } from 'vue';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	MapPinIcon,
} from '@heroicons/vue/24/outline';
import Swiper from 'swiper';
import 'swiper/css';

interface HeroCarouselCard {
	name: string;
	image: string;
	position: string;
	country: string;
	countryCode?: string;
	videoUrl?: string | null;
	skills: string[];
}

interface HeroCarouselProps {
	cards?: HeroCarouselCard[];
	variant?: 'full-width' | 'half-width';
	cardStyle?: 'compact' | 'full';
}

const props = withDefaults(defineProps<HeroCarouselProps>(), {
	cards: () => [],
	variant: 'half-width',
	cardStyle: 'full',
});

const isVariantFullWidth = computed(() => props.variant === 'full-width');
const isVariantHalfWidth = computed(() => props.variant === 'half-width');

type Mode = 'mobile' | 'desktop' | 'desktop-lg' | 'desktop-2xl';

const activeIndex = ref(0);
const swiperContainerRef = ref<HTMLElement | null>(null);
const swiper = shallowRef<Swiper | null>(null);
const swiperProgress = ref(-1);
const isSwiperAtEnd = ref(false);
const intendedEndTranslate = ref<number | null>(null);

const isLastCard = computed(() => {
	if (!swiper.value || props.cards.length <= 1) return true;

	const lastIndex = props.cards.length - 1;
	const isAtLastSlide = activeIndex.value >= lastIndex;
	const isAtEnd = swiper.value.isEnd || isSwiperAtEnd.value;

	return isAtLastSlide || isAtEnd;
});

const containerWidthClass = computed(() =>
	isVariantFullWidth.value ? 'xl:w-full' : 'xl:w-[25rem] 2xl:w-[36.25rem]',
);

const cardClasses = computed(() =>
	isVariantFullWidth.value
		? CARD_CLASSES['full-width']
		: CARD_CLASSES['half-width'],
);

const skillsSectionClasses = computed(() =>
	isVariantFullWidth.value ? 'lg:flex' : 'md:flex',
);

const CARD_CLASSES = {
	'full-width':
		'!h-[400px] !w-[290px] lg:!h-[387px] lg:!w-[283px] xl:!h-[491px] xl:!w-[360px] 2xl:!h-[600px] 2xl:!w-[440px] !justify-end lg:!justify-between',
	'half-width':
		'!h-[25rem] !w-[18.125rem] xl:!h-[32.5rem] xl:!w-[23.75rem] 2xl:!h-[48rem] 2xl:!w-[35rem] !justify-end md:!justify-between',
};

const SKILLS_LIMITS = {
	'full-width': { 'mobile': 3, 'desktop-lg': 3, 'desktop-2xl': 7 },
	'half-width': { 'mobile': 3, 'desktop': 7, 'desktop-2xl': 9 },
} as const;

const cardsWithSkills = computed(() => {
	const limits = isVariantFullWidth.value
		? SKILLS_LIMITS['full-width']
		: SKILLS_LIMITS['half-width'];

	const getSkills = (skills: string[], mode: Mode) => {
		const limit = (limits as Record<string, number>)[mode] ?? 3;
		return skills.length <= limit ? skills : [...skills.slice(0, limit), '...'];
	};

	return props.cards.map((card) => {
		const baseCard = {
			...card,
			skillsMobile: getSkills(card.skills, 'mobile'),
			skillsDesktopLg: getSkills(card.skills, 'desktop-lg'),
			skillsDesktop2xl: getSkills(card.skills, 'desktop-2xl'),
		};

		if (isVariantHalfWidth.value) {
			return {
				...baseCard,
				skillsDesktop: getSkills(card.skills, 'desktop'),
			};
		}

		return {
			...baseCard,
			skillsDesktop: [] as string[],
		};
	});
});

function nextCard() {
	if (!swiper.value || isLastCard.value) return;
	if (activeIndex.value < props.cards.length - 1) {
		activeIndex.value++;
		swiper.value.slideTo(activeIndex.value);

		// Store the intended end position when reaching the last slide
		if (activeIndex.value >= props.cards.length - 1) {
			nextTick(() => {
				if (swiper.value) {
					intendedEndTranslate.value = swiper.value.getTranslate();
				}
			});
		}
	}
}

function prevCard() {
	if (!swiper.value) return;
	if (activeIndex.value > 0) {
		activeIndex.value--;
		swiper.value.slideTo(activeIndex.value);
	}
}

// Fixed: Progress bar calculation for both modes
const progressBarStyle = computed(() => {
	const total = props.cards.length;
	if (total <= 1) return { width: '100%', left: '0%' };

	const segment = 100 / total;

	if (isLastCard.value) {
		const lastSegmentIndex = total - 1;
		return { width: `${segment}%`, left: `${lastSegmentIndex * segment}%` };
	}

	let progress =
		swiperProgress.value >= 0
			? swiperProgress.value
			: activeIndex.value / (total - 1);

	progress = Math.min(Math.max(progress, 0), 1);
	const segmentIndex = Math.floor(progress * (total - 1));

	return { width: `${segment}%`, left: `${segmentIndex * segment}%` };
});

const SWIPER_CONFIG = {
	'full-width': {
		slidesPerView: 'auto' as const,
		spaceBetween: 24,
		loop: false,
		resistanceRatio: 0,
		watchOverflow: true,
		touchReleaseOnEdges: true,
		slidesOffsetAfter: 0,
		breakpoints: {
			320: { spaceBetween: 16, slidesOffsetAfter: 50 },
			1024: { spaceBetween: 20, slidesOffsetAfter: 50 },
			1280: { spaceBetween: 24, slidesOffsetAfter: 50 },
			1536: { spaceBetween: 24, slidesOffsetAfter: 150 },
		},
	},
	'half-width': {
		slidesPerView: 'auto' as const,
		spaceBetween: 32,
		loop: false,
		resistanceRatio: 0,
		watchOverflow: true,
		touchReleaseOnEdges: true,
		slidesOffsetAfter: 380,
		breakpoints: {
			320: { spaceBetween: 16, slidesOffsetAfter: 150 },
			1024: { spaceBetween: 24, slidesOffsetAfter: 250 },
			1280: { spaceBetween: 32, slidesOffsetAfter: 380 },
			1536: { spaceBetween: 32, slidesOffsetAfter: 520 },
		},
	},
};

onMounted(async () => {
	await nextTick();
	if (!swiperContainerRef.value) return;

	const config = isVariantFullWidth.value
		? SWIPER_CONFIG['full-width']
		: SWIPER_CONFIG['half-width'];

	swiper.value = new Swiper(swiperContainerRef.value, {
		...config,
		on: {
			setTranslate(_, translate) {
				if (!swiper.value || props.cards.length <= 1) {
					swiperProgress.value = -1;
					return;
				}

				const maxT = swiper.value.maxTranslate();
				const minT = swiper.value.minTranslate();

				if (maxT === minT) {
					swiperProgress.value = 1;
					isSwiperAtEnd.value = true;
					return;
				}

				const progress = (translate - minT) / (maxT - minT);
				swiperProgress.value = Math.min(Math.max(progress, 0), 1);

				// More sensitive end detection for full-width mode
				const endThreshold = isVariantFullWidth.value ? 0.95 : 0.99;
				isSwiperAtEnd.value = swiperProgress.value >= endThreshold;
			},

			slideChange(sw) {
				activeIndex.value = sw.activeIndex;
				swiperProgress.value = -1;

				const last = props.cards.length - 1;
				isSwiperAtEnd.value = sw.activeIndex >= last;

				// Store the translate position when reaching the last slide via slideTo
				// This represents the intended end position that matches button navigation
				if (sw.activeIndex >= last) {
					intendedEndTranslate.value = sw.getTranslate();
				}
			},

			touchEnd(sw) {
				const last = props.cards.length - 1;
				if (sw.activeIndex > last) {
					sw.slideTo(last);
					isSwiperAtEnd.value = true;
					return;
				}

				// Only snap back if we've dragged beyond the intended end position
				if (sw.activeIndex >= last && intendedEndTranslate.value !== null) {
					const currentTranslate = sw.getTranslate();

					// If we've dragged beyond the intended end position (too far left),
					// snap back to that position
					if (currentTranslate < intendedEndTranslate.value) {
						sw.setTranslate(intendedEndTranslate.value);
						sw.updateSlidesClasses();
						isSwiperAtEnd.value = true;
					} else {
						isSwiperAtEnd.value = sw.activeIndex >= last;
					}
				} else {
					isSwiperAtEnd.value = sw.activeIndex >= last;
				}
			},

			reachEnd() {
				isSwiperAtEnd.value = true;
			},

			fromEdge() {
				if (swiper.value) {
					isSwiperAtEnd.value = swiper.value.isEnd;
				}
			},
		},
	});

	if (swiper.value) {
		const last = props.cards.length - 1;
		isSwiperAtEnd.value =
			swiper.value.isEnd || swiper.value.activeIndex >= last;

		// Initialize intended end position if we're already at the last slide
		if (swiper.value.activeIndex >= last) {
			nextTick(() => {
				if (swiper.value) {
					intendedEndTranslate.value = swiper.value.getTranslate();
				}
			});
		}
	}
});
</script>

<style scoped>
.swiper-container {
	width: 100%;
	height: auto;
}

.swiper-wrapper {
	display: flex;
	align-items: center;
	transition-timing-function: ease-in-out;
}

.swiper-slide {
	flex-shrink: 0;
}
</style>
